import { asKey, colVal, eq } from "src/utils/fields";
import { Comparator, Condition, Field, SearchState } from "../types";
import { selectors } from "./useParamsStateSelectors";

export function useParamsStateModifiers() {
  return {
    filterDegrouped,
    setAndAddFilters,
    removeFilters,
    filterOnGroups,
    byWeekByDow,
    byDowByHour,
    byHourByMinute,
  };
}

function timeByTime({
  state,
  f1,
  f2,
}: {
  state: SearchState;
  f1: Field;
  f2: Field;
}): SearchState {
  const filters = [
    ...(state.filters || []).filter(
      (f) =>
        !(f.target === f1.target && f.attribute === f1.attribute) &&
        !(f.target === f2.target && f.attribute === f2.attribute)
    ),
    {
      key: `${f1.target}.${f1.attribute}`,
      target: f1.target,
      attribute: f1.attribute,
      comparison: {
        "NOT IN": ["__VZBL::null__"],
      },
    },
    {
      key: `${f2.target}.${f2.attribute}`,
      target: f2.target,
      attribute: f2.attribute,
      comparison: {
        "NOT IN": ["__VZBL::null__"],
      },
    },
  ];
  return {
    ...state,
    filters,
    groupBys: [f1, f2],
    aggregations: [
      {
        target: "Event",
        attribute: "uuid",
        reducer: "count",
      },
    ],
  };
}

function byWeekByDow({ state }: { state: SearchState }) {
  return timeByTime({
    state,
    f1: { target: "Event", attribute: "DayOfWeek" },
    f2: { target: "Event", attribute: "Week" },
  });
}

function byDowByHour({ state }: { state: SearchState }) {
  return timeByTime({
    state,
    f1: { target: "Event", attribute: "Hour" },
    f2: { target: "Event", attribute: "DayOfWeek" },
  });
}

function byHourByMinute({ state }: { state: SearchState }) {
  return timeByTime({
    state,
    f1: { target: "Event", attribute: "Minute" },
    f2: { target: "Event", attribute: "Hour" },
  });
}

function setAndAddFilters({
  state,
  ...props
}: {
  state: SearchState;
  property: Field;
  operator: "IN" | "NOT IN";
  value: string;
}): Condition[] {
  const select = selectors();
  return [
    ...select.getFilters(state, (field) => !eq(field, props.property)),
    {
      attribute: props.property.attribute,
      target: props.property.target,
      key: asKey(props.property),
      comparison: {
        [props.operator]:
          select.hasAttributeFilter(state, props.property) &&
          select.getAttributeFilterOperator(state, props.property) ===
            props.operator
            ? [
                ...select.getAttributeFilterValues(state, props.property),
                props.value,
              ]
            : [props.value],
      } as Comparator,
    },
  ];
}

function filterOnGroups({
  state,
  property,
  value,
}: {
  state: SearchState;
  property: Field;
  value: string;
}): SearchState {
  const groupBys = (state.groupBys || []).filter(
    (group) => !eq(group, property)
  );
  const filters = removeFilters({ state, property });
  filters.push({
    key: asKey(property),
    ...property,
    comparison: { IN: [value] } as Comparator,
  });
  return { ...state, filters, groupBys };
}

function filterDegrouped({
  state,
  record,
  properties = state.groupBys || [],
}: {
  state: SearchState;
  record: Record<string, any>;
  properties?: Field[];
}): SearchState {
  let newState: SearchState = state;
  properties.forEach((gb) => {
    newState = filterOnGroups({
      state: newState,
      property: gb,
      value: `${colVal(record, asKey(gb))}`,
    });
  });
  return {
    ...newState,
    groupBys: [],
    having: [],
  };
}

function removeFilters({
  state,
  property,
  value,
}: {
  state: SearchState;
  property: Field;
  value?: string;
}): Condition[] {
  const select = selectors();
  const filters = select.getFilters(state, (field) => !eq(field, property));
  const operator = select.getAttributeFilterOperator(state, property);
  const values =
    value === undefined
      ? []
      : select
          .getAttributeFilterValues(state, property)
          .filter((v) => v !== value);

  // remove filter altogether
  if (values.length === 0) return filters;
  // vs removing a value from the filter
  return [
    ...filters,
    {
      key: asKey(property),
      attribute: property.attribute,
      target: property.target,
      comparison: { [operator]: values } as Comparator,
    },
  ];
}
